import Card from "@ui-kit/data-display/Card/Card.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { enums } from "@shared/constants";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import { useTranslation } from "@shared/utils/internationalization";
import React from "react";
import TicketTypesList from "@shared/modules/events/components/tickets/TicketTypesList/TicketTypesList.web";
import DetailPageModal from "@/components/organisms/purchases/modals/DetailPageModal";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import AttractionEventModal from "@/components/organisms/modals/attractionEventModal/AttractionEventModal";
import { IPublicBasket } from "@app/core/src/app-contexts/public/features/basket/interfaces/IPublicBasket";
import { IVenueBasket } from "@app/core/src/app-contexts/dashboard/features/box-office/basket/interfaces/IVenueBasket";
import { getPublicPurchasePlatform } from "@shared/modules/basket/utils/basket-utils";
import UserInteractionLogger from "@shared/lib/analytics/internal/UserInteractionLogger";
import { SpecialParentConfig } from "@/../core/src/app-contexts/dashboard/features/events/data/types/events";

export interface DateTimeSelectorProps {
  event: PublicBaseEventSerializer;
  sectionTitle: string;
  sectionDescription: string;
  buttonText: keyof typeof enums.INITIATE_PURCHASE_BUTTON_DESCRIPTION_MAP_BY_ID;
  eventIdArray: number[];
  specialEventsArray: Array<SpecialParentConfig>;
  itemIdArray: number[];
  venueSlug: string;
  promptForQuantity: boolean;
  ticketTypeSelectionRequired: boolean;
  itemType: number;
  basket: IPublicBasket | IVenueBasket;
  isSoldOut?: boolean;
}

const DateTimeSelector: React.FC<DateTimeSelectorProps> = ({
  event,
  sectionTitle,
  sectionDescription,
  buttonText,
  eventIdArray,
  specialEventsArray,
  itemIdArray,
  venueSlug,
  promptForQuantity,
  ticketTypeSelectionRequired,
  itemType,
  basket,
  isSoldOut = false,
}) => {
  const { t } = useTranslation("purchases");

  const PURCHASE_BUTTON_ENUM =
    enums.INITIATE_PURCHASE_BUTTON_DESCRIPTION_MAP_BY_ID;

  // attraction modal variables
  const { isOpen, onOpen, onClose } = useDisclosure();

  // single event modal variables
  const {
    isOpen: isTicketModalOpen,
    onOpen: onTicketModalOpen,
    onClose: onTicketModalClose,
  } = useDisclosure();

  const handleButtonClick = () => {
    if (itemType === enums.ATTRACTION_CHILD_TYPE_MAP.SINGLE_EVENT.id) {
      UserInteractionLogger.pageView({
        label: enums.INTERACTION_LABEL_MAP.EVENT_PAGE.id,
        event_id: eventIdArray[0],
        venue_id: event.venue?.id,
        custom_params: {
          purchase_source_platform: getPublicPurchasePlatform(),
        },
      });
      onTicketModalOpen();
    } else {
      onOpen();
    }
  };

  return (
    <>
      <Card
        w="full"
        display="flex"
        flexDirection="column"
        rounded={{ base: 0, sm: "lg" }}
      >
        <Flex
          justifyContent="space-between"
          alignItems={{ base: "flex-start", xs: "center" }}
          direction={{ base: "column", xs: "row" }}
        >
          <Text
            textAlign="left"
            wordBreak="break-word"
            fontSize={{ base: "md", sm: "lg" }}
            fontWeight="bold"
            color="primary.600"
          >
            {sectionTitle}
          </Text>
          <Box minW="element.lg" />

          <Button
            data-testid="date-time-selector-find-tickets"
            colorPalette="primary"
            variant="solid"
            onClick={handleButtonClick}
            mt={{ base: "md", xs: "none" }}
            minWidth={{ base: "100%", xs: "147px" }}
            isDisabled={isSoldOut}
          >
            {isSoldOut
              ? t("Sold Out", { ns: "common" })
              : t(PURCHASE_BUTTON_ENUM[buttonText].name, { ns: "constants" }) ||
                t("Find Tickets")}
          </Button>
        </Flex>

        {sectionDescription && (
          <ReadMore
            openReadMoreText={t("Read More")}
            closeReadMoreText={t("Show Less")}
            descriptionHtml={sectionDescription}
          />
        )}
      </Card>

      <AttractionEventModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onClose}
        title={sectionTitle}
        event={event}
        venue={event.venue}
        calendarProps={{ venueSlug, eventIdArray, specialEventsArray, basket }}
        promptForQuantity={promptForQuantity}
        ticketTypeSelectionRequired={ticketTypeSelectionRequired}
      />

      <DetailPageModal
        title={sectionTitle}
        isOpen={isTicketModalOpen}
        onClose={onTicketModalClose}
        itemListElement={
          <TicketTypesList
            eventId={eventIdArray[0]}
            showItemIds={itemIdArray}
          />
        }
      />
    </>
  );
};

export default DateTimeSelector;
